import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../api/endpoints/platform';
import type { TenantLevelOption } from '../components/scoped/levelColumns';
import { useMyProfile } from './useMyProfile';
import { useResolvedTenantLevels } from './useResolvedTenantLevels';

/** Referencia estable para no rearmar las columnas en cada render. */
const NO_LEVELS: TenantLevelOption[] = [];

/**
 * Niveles que define la APLICACIÓN de un tenant (`{ level, displayName }`) — p.ej. LEVEL_1 = "Grupos"
 * en `agents`, LEVEL_1/2/3 en SureKeep. Son la fuente para rotular cualquier UI POR NIVEL (columnas,
 * menús, filtros) sin hardcodear el nombre: cada tenant llama distinto a su LEVEL_1.
 *
 * Dos hooks gemelos, misma forma de retorno, uno por contexto — mismo patrón que
 * `useTenantIdentifier` / `useMyTenantIdentifier`:
 *   - `useTenantLevels(tid)`   → contexto PLATFORM (drill-down): del `TenantDetail` (`getTenant`).
 *   - `useMyTenantLevels()`    → contexto TENANT-ADMIN: de `/me/profile`, que NO tiene `getTenant`
 *                                (es platform-only, exige PLATFORM_TENANT_VIEW).
 *
 * Ambos devuelven `[]` mientras cargan y ante un backend que aún no manda el campo, así el consumidor
 * siempre tiene un array estable: sin niveles simplemente no se pintan columnas por nivel.
 */
export function useTenantLevels(tenantId: string): { levels: TenantLevelOption[] } {
    const { data } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: !!tenantId,
    });
    // Mismo respaldo que el resolvedor del tenant visto: sin `tenant.levels`, los de su aplicación.
    const levels = useResolvedTenantLevels(data);

    return { levels: levels ?? NO_LEVELS };
}

/** Gemelo de `useTenantLevels` para el PROPIO tenant del usuario logueado */
export function useMyTenantLevels(): { levels: TenantLevelOption[] } {
    const { data } = useMyProfile((profile) => profile.levels);

    return { levels: data ?? [] };
}
